/**
 * The Scheduling board's read model (GET /scheduling/board), as
 * backend/src/services/scheduling/schedulingBoard.js returns it: one row per
 * building, tabs that are eMerge states (owner ruling 2026-10-05), and the
 * Monday `scheduling` row only as a read-only reference.
 */
import api from '../../services/api';
import type { ApiResult, NextPlan } from '../finance/financeApi';
import type { BoardPage, BoardTabDef } from '../../components/board';
import type { MarkerShape } from '../../components/tide';

export type EmergeStateKey = 'closed' | 'blocked' | 'confirmed' | 'planned' | 'no-plan';

/** A building's current manifest link, held by eMerge (facility_manifest_links). */
export interface ManifestLink {
  url: string;
  source: 'EMERGE';
  /** Provenance only: typed in eMerge, or copied once from Monday. eMerge holds it either way. */
  origin: 'EMERGE' | 'MONDAY_IMPORT';
  origin_text: string;
  set_at: string | null;
  set_by: string | null;
  version: number;
  stored_on: string;
}

export interface SchedVisitFact { source: 'EMERGE'; on: string; what: string }

/** The Monday `scheduling` row for the building (its newest): shown, never used to schedule. */
export interface MondayReference {
  scheduling_id: string;
  /** How many Monday items name this building; the newest is shown. */
  items: number;
  status: { word: string; color: string | null; known: boolean } | null;
  provider: { monday_user_id: string; name: string | null } | null;
  last_visit: string | null;
  next_visit: string | null;
  tentative_next_visit: string | null;
  census_date: string | null;
  tm_follow_up: string | null;
  manifest_uploaded: string | null;
  residents_on_item: number;
  comments: string | null;
  updated_at: string | null;
  source: 'MONDAY';
  used_for_scheduling: false;
}

/** An eMerge date on the building's scheduling profile, with where it came from (provenance; eMerge is the authority either way). */
export interface ProfileDate { on: string; source: 'EMERGE'; origin: 'MONDAY_SEED' | 'EMERGE'; origin_text: string }

/** The building is on eMerge's schedule (facility_scheduling_profiles, 2026-10-05). */
export interface SchedulingProfile {
  id: string;
  status: 'ACTIVE' | 'CLOSED';
  closure: { kind: 'DROPPED' | 'DO_NOT_SCHEDULE'; reason: string | null; closed_at: string | null } | null;
  tentative_next_visit: ProfileDate | null;
  census: ProfileDate | null;
  tm_follow_up: ProfileDate | null;
  origin: 'MONDAY_SEED' | 'EMERGE';
  origin_text: string;
  source: 'EMERGE';
  version: number;
}

export interface SchedulingBoardRow {
  id: string;
  /**
   * BUILDING = an eMerge building row (a scheduling profile, coverage, a plan or a blocker);
   * MONDAY_ONLY = a building only Monday's board lists (reference tab, outside Main table);
   * UNLINKED = a Monday item that names no building (enterprise-only reference tab).
   */
  kind: 'BUILDING' | 'MONDAY_ONLY' | 'UNLINKED';
  facility: { id: string; name: string | null; city: string | null; region: string | null } | null;
  emerge_state: { key: EmergeStateKey; word: string; color: string; marker: MarkerShape; source: 'EMERGE' } | null;
  next_visit: { source: 'EMERGE'; on: string; state: 'PLANNED' | 'CONFIRMED'; what: string; provider: { employee_id: string; name: string | null } | null } | null;
  last_visit: { value: SchedVisitFact | null; facts: SchedVisitFact[] };
  coverage: { employee_id: string; name: string | null; coverage_type: string; source: 'EMERGE' }[];
  blocker: { id: string; reason: string; recorded_at: string; recorded_by_name?: string | null; source: 'EMERGE' } | null;
  /**
   * The building's manifest link as eMerge holds it (MAN-4, 2026-10-05). null:
   * eMerge holds none, or holds it as cleared (`manifest_link_state` says which).
   */
  manifest_link: ManifestLink | null;
  manifest_link_state?: 'EMERGE' | 'CLEARED' | 'MONDAY_REFERENCE_ONLY' | 'NONE';
  /** The version to name when changing the link; 0 when eMerge holds no row yet. */
  manifest_link_version?: number;
  /** Monday's column, a labelled reference only. Never the link itself. */
  monday_manifest_link?: { url: string; stored_on: string; source: 'MONDAY'; synced: true; reference: true } | null;
  credentialing: { word: string; source: 'MONDAY'; enforced: false } | null;
  monday: MondayReference | null;
  next_plan: NextPlan | null;
  expedited_open: number;
  /** null: eMerge holds no scheduling profile for the building. */
  profile?: SchedulingProfile | null;
}
export interface FilterOption { id: string; name: string; count: number }
export type SchedulingTabDef = BoardTabDef;
export interface SchedulingBoardPage extends Omit<BoardPage<SchedulingBoardRow>, 'mode' | 'groups'> {
  mode: 'EMERGE';
  /** The five eMerge states' sum and Main table, from the same counts: equal by construction, stated on screen. */
  buckets: { keys: EmergeStateKey[]; sum: number; main: number };
  filters: { provider: string | null; building: string | null; q: string | null };
  filter_options: { providers: FilterOption[]; buildings: FilterOption[] };
  not_mirrored: string[];
  can: { plan: boolean };
}

export interface SchedulingBoardQuery { tab?: string; q?: string; provider?: string; building?: string; sort?: string | null; dir?: 'asc' | 'desc'; page?: number }

export function boardQueryString(p: Record<string, string | number | null | undefined>): string {
  const qp = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== null && v !== undefined && v !== '' && !(k === 'page' && Number(v) <= 1)) qp.set(k, String(v));
  const s = qp.toString();
  return s ? `?${s}` : '';
}

export const schedulingBoardApi = {
  board: async (p: SchedulingBoardQuery): Promise<ApiResult<SchedulingBoardPage>> =>
    (await api.get(`/scheduling/board${boardQueryString({ tab: p.tab, q: p.q, provider: p.provider, building: p.building, sort: p.sort, dir: p.sort ? p.dir : null, page: p.page })}`)) as ApiResult<SchedulingBoardPage>,
  /** Set the building's current manifest link, or clear it with null. `version` is the one read (0 when none). */
  setManifestLink: async (facilityId: string, url: string | null, version: number): Promise<ApiResult<{ url: string | null; cleared: boolean; version: number }>> =>
    (await api.put(`/scheduling/facilities/${encodeURIComponent(facilityId)}/manifest-link`, { url, version })) as ApiResult<{ url: string | null; cleared: boolean; version: number }>,
};
